import React, { useState, useEffect } from 'react';
import './DocumentViewer.css';

function DocumentViewer({ documentUrl, pageNumber, onClose, isModal = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [useAlternativeViewer, setUseAlternativeViewer] = useState(false);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);

  // Extract document info from URL
  const parseDocumentUrl = (url) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const pageMatch = urlObj.hash.match(/#page=(\d+)/);
      const page = pageMatch ? parseInt(pageMatch[1], 10) : 1;

      // Get file extension to determine document type
      const fileExt = fileName.split('.').pop().toLowerCase();

      return {
        fileName,
        page,
        baseUrl: url.replace(urlObj.hash, ''),
        fullUrl: url,
        fileType: fileExt
      };
    } catch (e) {
      return null;
    }
  };

  // Get document type and setup viewer URL
  const setupDocumentViewer = (url, page = 1) => {
    if (!url) return;

    const docInfo = parseDocumentUrl(url);
    if (!docInfo) {
      setError('Invalid document URL');
      setLoading(false);
      return;
    }

    setDocumentInfo(docInfo);
    // Use the page from URL if available, otherwise use the passed page parameter
    const targetPage = docInfo.page || page || 1;
    setCurrentPage(targetPage);

    let finalViewerUrl = '';

    // Handle different document types
    switch (docInfo.fileType) {
      case 'pdf':
        // For PDFs, we can use either direct embed or Google Docs viewer
        // Direct PDF embed with page support
        if (docInfo.baseUrl.startsWith('https://')) {
          // Try direct PDF embedding with page anchor
          finalViewerUrl = `${docInfo.baseUrl}#page=${targetPage}`;

          // Alternative: Use Google Docs viewer for better compatibility
          // Note: Google Docs viewer doesn't support page navigation well for PDFs
          // finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        } else {
          // Use Google Docs viewer for non-HTTPS URLs
          finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        }
        break;

      case 'docx':
      case 'doc':
        // For Word documents, we have multiple viewer options
        if (useAlternativeViewer) {
          // Use Google Docs viewer as alternative
          finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
          // Try to add page parameter (though Google Docs viewer may not honor it)
          if (targetPage > 1) {
            // For Google Docs viewer, we can try adding a page anchor
            finalViewerUrl += `#:0.page.${targetPage}`;
          }
        } else {
          // Use Microsoft Office Online viewer (primary option)
          if (targetPage > 1) {
            // Office Online viewer with page parameter
            finalViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docInfo.baseUrl)}&wdStartOn=${targetPage}`;
          } else {
            finalViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docInfo.baseUrl)}`;
          }
        }
        break;

      case 'ppt':
      case 'pptx':
        // For PowerPoint documents
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        if (targetPage > 1) {
          finalViewerUrl += `#slide=${targetPage}`;
        }
        break;

      case 'xls':
      case 'xlsx':
        // For Excel documents
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        break;

      default:
        // For other files, try Google viewer
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        break;
    }

    setViewerUrl(finalViewerUrl);
    setLoading(false);
  };

  useEffect(() => {
    if (documentUrl) {
      // Reset fallback flag for new document
      setHasTriedFallback(false);
      setupDocumentViewer(documentUrl, pageNumber);
    }
  }, [documentUrl, pageNumber, useAlternativeViewer]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1) {
      setCurrentPage(newPage);
      // For PDF documents with page support, update the viewer URL
      if (documentInfo && documentInfo.fileType === 'pdf') {
        if (documentInfo.baseUrl.startsWith('https://')) {
          // Direct PDF with page anchor
          const newUrl = `${documentInfo.baseUrl}#page=${newPage}`;
          setViewerUrl(newUrl);
        } else {
          // Google Docs viewer (page navigation might not work perfectly)
          const newUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(documentInfo.baseUrl)}&embedded=true#page=${newPage}`;
          setViewerUrl(newUrl);
        }
      } else if (documentInfo && (documentInfo.fileType === 'docx' || documentInfo.fileType === 'doc')) {
        // For DOCX, try to update page (though Google viewer might not honor it)
        const newUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(documentInfo.baseUrl)}&embedded=true#page=${newPage}`;
        setViewerUrl(newUrl);
      }
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    // If this is a PDF that failed and we haven't tried the fallback yet
    if (documentInfo && documentInfo.fileType === 'pdf' && !hasTriedFallback) {
      console.log('PDF failed to load, trying .doc fallback...');
      tryDocFallback();
    } else {
      setError('Failed to load document. The document may not be accessible or the format is not supported.');
      setLoading(false);
    }
  };

  const tryDocFallback = () => {
    if (!documentInfo || hasTriedFallback) return;

    setHasTriedFallback(true);
    setLoading(true);
    setError(null);

    // Replace .pdf with .doc in the base URL
    const fallbackUrl = documentInfo.baseUrl.replace(/\.pdf$/i, '.doc');
    const fallbackFullUrl = documentInfo.fullUrl.replace(/\.pdf/i, '.doc');

    // Update document info with new URLs and file type
    const fallbackDocInfo = {
      ...documentInfo,
      baseUrl: fallbackUrl,
      fullUrl: fallbackFullUrl,
      fileType: 'doc',
      fileName: documentInfo.fileName.replace(/\.pdf$/i, '.doc')
    };

    setDocumentInfo(fallbackDocInfo);

    // Setup viewer with the new DOC file
    setupDocumentViewerWithInfo(fallbackDocInfo, currentPage);
  };

  const setupDocumentViewerWithInfo = (docInfo, page = 1) => {
    const targetPage = docInfo.page || page || 1;
    setCurrentPage(targetPage);

    let finalViewerUrl = '';

    // Handle different document types (same logic as setupDocumentViewer)
    switch (docInfo.fileType) {
      case 'pdf':
        if (docInfo.baseUrl.startsWith('https://')) {
          finalViewerUrl = `${docInfo.baseUrl}#page=${targetPage}`;
        } else {
          finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        }
        break;

      case 'docx':
      case 'doc':
        if (useAlternativeViewer) {
          finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
          if (targetPage > 1) {
            finalViewerUrl += `#:0.page.${targetPage}`;
          }
        } else {
          if (targetPage > 1) {
            finalViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docInfo.baseUrl)}&wdStartOn=${targetPage}`;
          } else {
            finalViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docInfo.baseUrl)}`;
          }
        }
        break;

      case 'ppt':
      case 'pptx':
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        if (targetPage > 1) {
          finalViewerUrl += `#slide=${targetPage}`;
        }
        break;

      case 'xls':
      case 'xlsx':
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        break;

      default:
        finalViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(docInfo.baseUrl)}&embedded=true`;
        break;
    }

    setViewerUrl(finalViewerUrl);
    setLoading(false);
  };

  const handlePrevPage = () => {
    handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    handlePageChange(currentPage + 1);
  };

  if (!documentUrl) {
    return null;
  }

  return (
    <div className={`document-viewer ${isModal ? 'document-viewer-modal' : ''}`}>
      {!isModal && (
        <div className="document-viewer-header">
          <div className="document-info">
            <h4>Document Viewer</h4>
            {documentInfo && (
              <span className="document-name">
                {documentInfo.fileName}
                {hasTriedFallback && documentInfo.fileType === 'doc' && (
                  <span className="fallback-indicator"> (DOC fallback)</span>
                )}
              </span>
            )}
          </div>
          <button
            className="document-viewer-close"
            onClick={onClose}
            aria-label="Close document viewer"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}

      <div className="document-viewer-content">
        {loading && (
          <div className="document-loading">
            <div className="loading-spinner"></div>
            <p>Loading document...</p>
          </div>
        )}

        {error && (
          <div className="document-error">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>{error}</p>
            <div className="error-actions">
              <button className="retry-button" onClick={() => setupDocumentViewer(documentUrl, currentPage)}>
                Retry
              </button>
              {documentInfo && documentInfo.fileType === 'pdf' && !hasTriedFallback && (
                <button className="fallback-button" onClick={tryDocFallback}>
                  Try .DOC version
                </button>
              )}
            </div>
          </div>
        )}

        {viewerUrl && !error && (
          <div className="document-display">
            <div className="document-iframe-container">
              {loading && (
                <div className="iframe-loading-overlay">
                  <div className="loading-spinner"></div>
                  <p>Loading document...</p>
                </div>
              )}
              <iframe
                src={viewerUrl}
                title={`Document Viewer - ${documentInfo?.fileName || 'Document'}`}
                className="document-iframe"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{ display: loading ? 'none' : 'block' }}
              />
            </div>
            {documentInfo && (
              <div className="document-info-bar">
                <div className="document-details">
                  <span className="document-name">{documentInfo.fileName}</span>
                  <span className="document-type">{documentInfo.fileType.toUpperCase()}</span>
                  {hasTriedFallback && documentInfo.fileType === 'doc' && (
                    <span className="fallback-badge">PDF→DOC</span>
                  )}
                </div>
                <div className="document-url-info">
                  <a
                    href={documentInfo.fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="source-link"
                  >
                    View Original
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

export default DocumentViewer;